// steps/tabs/meter-readings/subtabs/MeterReadingFilters.tsx
import { useChoices } from "@features/mx/device-management/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useActiveUtilityServices } from "@shared/hooks/useActiveUtililyServices";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { X } from "lucide-react";
import React, { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";

export interface MeterReadingFilters {
  meterType: string;
  service: string;
}

interface MeterReadingFiltersProps {
  filters: MeterReadingFilters;
  onFiltersChange: (filters: MeterReadingFilters) => void;
  onApplyFilters: (filters: MeterReadingFilters) => void;
  onClearFilters: () => void;
}

export const MissingReadingFilters: React.FC<MeterReadingFiltersProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}) => {
  const { data: meterTypes } = useChoices({ choice: "meter_type" });
  const { data: activeUtilityServices } = useActiveUtilityServices();
  const utilityServiceOptions = useMemo(() => {
    const options = [];

    if (activeUtilityServices && activeUtilityServices.length > 0) {
      const serviceOptions = activeUtilityServices.map((service) => ({
        label: service.name,
        value: service.name,
      }));
      options.push(...serviceOptions);
    }

    return options;
  }, [activeUtilityServices]);
  const meterTypeOptions = useMemo(() => {
    if (!meterTypes || !Array.isArray(meterTypes)) return [];
    return meterTypes.map((type) => ({
      value: String(type.key),
      label: type.value,
    }));
  }, [meterTypes]);

  const createFormFields = (): FormField[] => [
    {
      name: "meterType",
      label: "Meter Type",
      type: "select",
      required: false,
      placeholder: "All Types",
      options: meterTypeOptions,
      classes: FIELD_CLASSES,
    },
    {
      name: "service",
      label: "Service",
      type: "select",
      required: false,
      placeholder: "All Services",
      options: utilityServiceOptions,
      classes: FIELD_CLASSES,
    },
  ];

  // Create form instance
  const form: UseFormReturn<MeterReadingFilters> =
    FormService.createForm<MeterReadingFilters>({
      fields: createFormFields(),
      defaultValues: filters,
    });

  // Handle form submission (Apply Filters)
  const handleSubmit = (data: MeterReadingFilters) => {
    const currentValues = form.getValues();
    onApplyFilters(currentValues);
  };

  // Handle form reset (Clear Filters)
  const handleReset = () => {
    const defaultFilters: MeterReadingFilters = {
      meterType: "all",
      service: "all",
    };

    form.reset(defaultFilters);
    onClearFilters();
  };

  const renderCustomFooter = () => (
    <div className="flex justify-between items-center pt-6 border-t border-gray-200">
      <Button
        onClick={handleReset}
        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
      >
        <X className="h-4 w-4" />
        Clear Filters
      </Button>

      <Button
        type="submit"
        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
      >
        Apply Filters
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={createFormFields()}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        config={{
          showSubmitButton: false, // Using custom footer
          showResetButton: false, // Using custom footer
          layout: "vertical",
          spacing: "medium",
        }}
        gridColumns={4}
        responsiveColumns={{ sm: 1, md: 2, lg: 4 }}
        className="space-y-6"
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};
